import { CommerceEnv, DeployedEnv, FeatureConfig, HostInfo } from '@typesApp/Config'
import { apiHostConfig } from './apihost'
import { apiKeyConfig } from './apikey'
import { bundleHostList } from './bundlehost'
import { featureConfig } from './feature'

const safeEnv = (key: string, defaultValue?: string) => {
  const value = process.env[key]
  if (value === undefined) {
    if (defaultValue === undefined) {
      throw new Error(`Missing env variable ${key}`)
    }
    return defaultValue
  }
  return value
}

export const getEnv = (name: string, defaultValue?: string): string => {
  const env = safeEnv('REACT_APP_HOST_ENV', '')
  let baseName = name
  if (env) {
    baseName = `${name}_${env}` // e.g. REACT_APP_API_HOST_stage

    // fallback if env not defined for this host env
    if (process.env[baseName] === undefined) {
      baseName = name
    }
  }
  return safeEnv(baseName, defaultValue)
}

/**
 * IMPORTANT:
 *
 * When onboarding a new brand follow these steps to add the brand configuration:
 *
 * 1. Add the B/E apiHost URLs to the JSON files in the apihost folder
 * 2. Add the F/E bundle host URLs to the JSON files in the bundlehost folder
 * 3. Add the F/E api keys to applicable endpoints to the JSON files in the apikey folder
 * 4. (not required but recommended) Add a unit test for the new brand, just create
 *    from one f the existing ones (e.g. config-lcr-ca.test.ts)
 *
 * No changes are required on this file.
 */

/**
 * B/E apiHost configuration for local
 */
const localHostMap = {
  none: {
    apiHost: apiHostConfig.DEV,
    apiKey: apiKeyConfig.DEV,
    feature: featureConfig.DEV,
    env: DeployedEnv.UNKNOWN,
  },

  'localhost:8000': {
    apiHost: apiHostConfig.STAGE,
    apiKey: apiKeyConfig.STAGE,
    feature: featureConfig.STAGE,
    env: DeployedEnv.LOCAL,
  },

  'localhost:8000:dev': {
    apiHost: apiHostConfig.DEV,
    apiKey: apiKeyConfig.DEV,
    feature: featureConfig.DEV,
    env: DeployedEnv.LOCAL,
  },

  'localhost:8000:test': {
    apiHost: apiHostConfig.TEST,
    apiKey: apiKeyConfig.TEST,
    feature: featureConfig.TEST,
    env: DeployedEnv.LOCAL,
  },

  'localhost:8000:stage': {
    apiHost: apiHostConfig.STAGE,
    apiKey: apiKeyConfig.STAGE,
    feature: featureConfig.STAGE,
    env: DeployedEnv.LOCAL,
  },
} as Record<string, HostInfo>

/**
 * B/E apiHost configuration for each brand from the bundle host list.
 * Each ui bundle host maps to a specific apiHost entry for the brand.
 *
 * e.g.
 *  'ui.directbilling-stage.clearly.systems': {
 *     apiHost: apiHostConfig.STAGE,
 *     env: DeployedEnv.STAGE
 *   },
 **/
const brandHostMap = bundleHostList.reduce(
  (acc, hostEntry) => {
    const { host, env } = hostEntry

    // apiHostConfig is key'ed by DEV, TEST, STAGE, or PROD
    const apiEnv = `${env.toUpperCase()}`
    const apiHost = apiHostConfig[apiEnv]
    const apiKey = apiKeyConfig[apiEnv]
    const feature = featureConfig[apiEnv]

    // add an apiHost entry for each ui bundle host
    acc[host] = {
      apiHost,
      apiKey,
      feature,
      env,
    }
    return acc
  },
  {} as Record<string, HostInfo>
)

const hostMap = {
  ...localHostMap,
  ...brandHostMap,
}

const normalizeHost = (url = ''): string => url?.replace(/^https?:\/\//, '')?.replaceAll('/', '')

const getHostInfo = (): HostInfo => {
  const host = normalizeHost(window.directBilling?.env?.host)
  const isLocal = host?.indexOf('localhost') !== -1
  const hostSuffix = window.directBilling?.env?.localEnv || ''

  // use suffix (dev, test, stage) only on local
  const hostUrl = (isLocal && hostSuffix ? `${host}:${hostSuffix}` : host) || 'none'
  return hostMap[hostUrl] || hostMap['localhost:8000']
}

export const config = {
  /**
   * B/E api host for this brand
   */
  getApiHost: (): string => {
    const hostInfo = getHostInfo()
    return hostInfo.apiHost[window.directBilling?.env?.brand ?? 'cle']
  },

  /**
   * API Key if available
   */
  getApiKey: (apiName: string): string => {
    const hostInfo = getHostInfo()
    const keys = hostInfo.apiKey?.keys?.[window.directBilling?.env?.brand ?? '']
    return keys?.[apiName]
  },

  /**
   * Deployed environment, LOCAL, DEV, TEST, STAGE, PROD
   */
  getDeployedEnv: (): DeployedEnv => {
    const hostUrl = normalizeHost(window.directBilling?.env?.host || 'none')
    return hostMap[hostUrl]?.env || DeployedEnv.UNKNOWN
  },

  /**
   * Commerce env, dev, int, uat, prod
   */
  getCommerceEnv: (): CommerceEnv => {
    const hostInfo = getHostInfo()
    return hostInfo.apiHost.commerceEnv
  },

  /**
   * Feature config for this brand, may be undefined
   */
  getFeatures: (): FeatureConfig | undefined => {
    const hostInfo = getHostInfo()
    return hostInfo?.feature?.[window.directBilling?.env?.brand ?? '']
  },

  /**
   * Nullify cut off time, e.g. 23:59:59
   */
  nullifyCutoffTime: getEnv('REACT_APP_NULLIFY_CUTOFF_TIME', '23:59:59'),

  /**
   * Static assets host
   */
  getAssetsHost: (): string => {
    let host = window.directBilling?.env?.host ?? 'https://ui.directbilling-dev.clearly.systems'
    const url = new URL(host)
    return `${url.protocol}//${url.hostname}${url.port ? ':' + url.port : ''}`
  },
}
